// Renders the customer's subscriptions for one provider.

import { useState } from 'react';
import { ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TruncatedText } from '@/components/shared/truncated-text';
import { cn } from '@/lib/utils';
import { CollapseBody, CollapseHeader } from './collapse-toggle';
import { HeaderLink } from './header-link';
import { hasText } from './has-text';
import { ShippingAddressSection } from './shipping-address-section';
import { statusStyle } from './status-style';
import { StatTile } from './stat-tile';
import type { LabelledValueDto, SubscriptionItemDto, WorkspaceSubscriptionDto } from './types';

/** The plan name's type; the header link measures an invisible copy of the name in it. */
const TITLE = 'text-[15px] font-medium leading-[1.1] text-text-body';

/** Grey label on the left, dark value on the right. */
function MetaRow({ label, value, bold }: LabelledValueDto & { bold?: boolean }) {
  return (
    <div className="flex w-full items-start justify-between gap-3 text-[12px] leading-[1.1]">
      <p className="shrink-0 text-gray-700">{label}</p>
      <TruncatedText text={value} lines={2} className={cn('min-w-0 text-right text-text-prose', bold && 'font-semibold')} />
    </div>
  );
}

function SubscriptionItem({ item }: { item: SubscriptionItemDto }) {
  const [open, setOpen] = useState(true);
  const [productsOpen, setProductsOpen] = useState(true);
  // A block of empty strings is no address, as on the order card.
  const address = hasText(item.shippingAddress) ? item.shippingAddress : null;

  return (
    <div className="flex w-full flex-col rounded-xl border border-gray-100 bg-white px-2 pb-3 pt-2 drop-shadow-[0_0_3px_rgba(0,0,0,0.06)]">
      <div className="flex w-full flex-col gap-2">
        <div className="grid grid-cols-1">
          <CollapseHeader open={open} label={item.name} onToggle={() => setOpen((prev) => !prev)} className="min-w-0 [grid-area:1/1]">
            <TruncatedText text={item.name} lines={2} className={cn(TITLE, 'min-w-0', item.url && 'mr-5')} />
          </CollapseHeader>
          {item.url && <HeaderLink title={item.name} href={item.url} label="View subscription" titleClassName={TITLE} />}
        </div>
        <span
          className={cn(
            'w-fit rounded-xl px-3 py-1 text-[12px] font-medium leading-[1.1]',
            statusStyle(item.status),
          )}
        >
          {item.status}
        </span>
      </div>

      <CollapseBody open={open}>
        <div className="flex w-full flex-col gap-4 pt-2">
          <div className="flex w-full items-center justify-between gap-3">
            <div className="flex flex-col gap-1.5 text-[12px] leading-[1.1]">
              <p className="text-gray-700">Created on</p>
              <p className="font-semibold text-text-prose">{item.createdOn}</p>
            </div>
            {item.quantity !== null && (
              <div className="flex flex-col items-center justify-center gap-1.5 text-[12px] leading-[1.1]">
                <p className="text-gray-700">Qty</p>
                <p className="font-semibold text-text-prose">{item.quantity}</p>
              </div>
            )}
            {item.amount && <StatTile value={item.amount} label={item.amountLabel} bordered />}
          </div>

          <div className="flex w-full items-center justify-between gap-3 text-[12px] leading-[1.1]">
            <p className="shrink-0 text-gray-700">Subscription ID</p>
            <TruncatedText text={item.id} lines={2} copyable className="min-w-0 text-right font-semibold text-text-prose" />
          </div>

          {item.details.length > 0 && (
            <div className="flex w-full flex-col gap-2.5">
              {item.details.map((detail) => (
                <MetaRow key={detail.label} label={detail.label} value={detail.value} bold />
              ))}
            </div>
          )}

          {address && (
            <div className="flex w-full flex-col border-t border-gray-300 pt-3">
              <ShippingAddressSection address={address} />
            </div>
          )}

          {item.products && item.products.length > 0 && (
            <div className="flex w-full flex-col border-t border-gray-300 px-1 pt-3">
              <CollapseHeader open={productsOpen} label="Product Details" variant="chevron" onToggle={() => setProductsOpen((prev) => !prev)}>
                <span className="flex items-center gap-1.5">
                  <span className="flex size-5 items-center justify-center rounded bg-text-prose">
                    <Package className="size-3.5 text-white" />
                  </span>
                  <span className="text-[12px] font-medium leading-[1.1] text-text-body">Product Details</span>
                </span>
              </CollapseHeader>
              <CollapseBody open={productsOpen}>
                <div className="flex w-full flex-col gap-2 pt-2">
                  {item.products.map((product) => (
                    <div key={product.name} className="flex w-full items-start justify-between gap-3 text-[12px] leading-[1.1]">
                      <div className="flex min-w-0 flex-col gap-1">
                        <TruncatedText text={product.name} lines={2} className="min-w-0 text-text-prose" />
                        {product.sku && <p className="text-[11px] leading-[1.1] text-gray-700">SKU {product.sku}</p>}
                      </div>
                      <p className="shrink-0 font-semibold text-text-prose">
                        {product.quantity !== null && `${product.quantity} × `}
                        {product.amount}
                      </p>
                    </div>
                  ))}
                </div>
              </CollapseBody>
            </div>
          )}

          {item.cancellationReason && (
            <div className="flex w-full flex-col gap-1 text-[12px] leading-[1.1]">
              <p className="text-gray-700">Cancellation reason</p>
              <p className="text-text-prose">{item.cancellationReason}</p>
            </div>
          )}
          {item.cancellationDate && <MetaRow label="Cancellation date" value={item.cancellationDate} />}
        </div>
      </CollapseBody>
    </div>
  );
}

export function SubscriptionCard({ subscription }: { subscription: WorkspaceSubscriptionDto }) {
  // Open while there are plans to show — data can stream in after mount — until the agent
  // toggles by hand, which then wins.
  const [manualOpen, setManualOpen] = useState<boolean | null>(null);
  const open = manualOpen ?? subscription.items.length > 0;
  const [index, setIndex] = useState(0);

  return (
    <div
      className="flex w-full flex-col border-l-4 border-solid bg-gray-100"
      style={{ borderColor: subscription.accentColor }}
    >
      <div className="flex w-full flex-col bg-white px-5 pb-4 pt-4">
        <CollapseHeader open={open} label={subscription.provider} variant="chevron" onToggle={() => setManualOpen(!open)} className="h-5">
          <div className="flex min-w-0 items-center gap-2">
            <img
              src={subscription.logo}
              alt={subscription.provider}
              className="size-5 shrink-0 rounded-full object-cover"
            />
            <p className="truncate text-[14px] font-semibold leading-[1.1] text-text-body">{subscription.provider}</p>
          </div>
        </CollapseHeader>
      </div>

      <CollapseBody open={open}>
        {/* The header's 16px padding plus this 6px keep the 22px gap the open card had. */}
        <div className="flex w-full flex-col gap-3 bg-white px-5 pb-5 pt-1.5">
          <div className="flex w-full items-start gap-2">
            <StatTile value={String(subscription.count)} label="Subscriptions" />
            <StatTile value={subscription.totalSpent} label="Total spent" />
          </div>
          {subscription.meta.map((entry) => (
            <MetaRow key={entry.label} label={entry.label} value={entry.value} />
          ))}
        </div>

        <div className="flex w-full flex-col gap-3 px-4 py-3">
          <div className="flex w-full items-center justify-between gap-2">
            <p className="text-[12px] font-semibold leading-[1.1] text-text-body">Subscriptions</p>
            {subscription.items.length > 1 && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setIndex((prev) => Math.max(0, prev - 1))}
                  disabled={index === 0}
                  className="text-gray-900"
                  aria-label="Previous subscription"
                >
                  <ChevronLeft className="size-[14px]" />
                </Button>
                <p className="text-[12px] font-semibold leading-[1.1] text-text-body">
                  {index + 1}/{subscription.items.length}
                </p>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setIndex((prev) => Math.min(subscription.items.length - 1, prev + 1))}
                  disabled={index >= subscription.items.length - 1}
                  className="text-gray-900"
                  aria-label="Next subscription"
                >
                  <ChevronRight className="size-[14px]" />
                </Button>
              </div>
            )}
          </div>
          {subscription.items[index] ? (
            <SubscriptionItem key={subscription.items[index].id} item={subscription.items[index]} />
          ) : (
            <p className="py-4 text-center text-[12px] leading-[1.1] text-gray-700">No subscriptions found</p>
          )}
        </div>
      </CollapseBody>
    </div>
  );
}
